import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useNotifications } from '../hooks/useNotifications';
import { all_routes } from 'router/all_routes';
import { formatRelativeTime } from 'utils/dateUtils';

dayjs.extend(relativeTime);

const NotificationDropdown: React.FC = () => {
  const { t } = useTranslation();
  const {
    notifications,
    pagination,
    loading,
    error,
    fetchNextPage,
    markAsRead,
    markAllAsRead
  } = useNotifications();
  const navigate = useNavigate();


  const unreadCount = notifications.filter(n => !n.isRead).length;

  const renderNotificationContent = (notif: any) => {
    if (notif.templateId && notif.dataTemplate) {
      try {
        const template = t(`notifications.templates.${notif.templateId}`, {
          returnObjects: true,
          ...notif.dataTemplate
        })

        if (typeof template === 'object' && template) {
          const obj = template as { title?: string; description?: string };
          if (obj.title && obj.description) {
            return {
              title: obj.title,
              message: obj.description
            };
          }
        } else if (typeof template === 'string') {
          return {
            title: notif.title || t(`notifications.default_titles.${notif.type || 'info'}`),
            message: notif.message
          };
        }
      } catch (error) {
        console.error('Error rendering template:', error);
      }
    }

    return {
      title: notif.title,
      message: notif.message
    };
  };

  const handleNotificationClick = (notif: any) => {
    markAsRead(notif._id);
    if (notif?.redirectTo && makeRedirectTo(notif.templateId)) {
      navigate(makeRedirectTo(notif.templateId) ?? "#");
    }
  };

  const makeRedirectTo = (key: string): string | null => {
    switch (key) {
      case 'NEW_MESSAGE_RECEIVED':
        return all_routes.messages;
      case 'ABSENCE_STUDENT_GUARDIAN':
        return all_routes.absenceStudent;
      case 'MARK_PUBLISHED_STUDENT':
        return all_routes.StudentMarks;
      case 'MARK_PUBLISHED_GUARDIAN':
        return all_routes.StudentMarks;
      case 'NEW_LEAVE_CREATE':
        return all_routes.leave;
      case 'LEAVE_APPROVED':
        return all_routes.myLeave;
      case 'LEAVE_REJECTED':
        return all_routes.myLeave;
      case 'NEW_STUDENT_EXIT_CREATE':
        return all_routes.studentExit;
      case 'STUDENT_EXIT_APPROVED':
        return all_routes.myStudentExit;
      case 'NEW_CASHBOX_CREATED':
        return all_routes.cashbox.today;
      default:
        return null;
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div className="text-center py-4"><span className="spinner-border" /></div>;
    }
    if (error) {
      return <p className="text-danger text-center py-3">{t('notifications.error_loading')}</p>;
    }
    if (!notifications.length) {
      return <p className="text-muted text-center py-3">{t('notifications.no_notifications')}</p>;
    }

    return notifications.map(notif => {
      const content = renderNotificationContent(notif);

      return (
        <div
          key={notif._id}
          className={`d-flex align-items-start mb-2 p-2 rounded ${notif.isRead ? 'bg-white text-muted' : 'bg-light fw-bold'}`}
          style={{ cursor: 'pointer', borderBottom: '1px solid #ddd' }}
          onClick={() => handleNotificationClick(notif)}
        >
          <div className="me-2 flex-shrink-0 bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
            {content.title?.charAt(0).toUpperCase() || 'N'}
          </div>
          <div className="flex-grow-1">
            <p className="mb-1" style={{ margin: 0 }}>{content.title}</p>
            <small className="d-block text-muted">{content.message}</small>
            <small className="text-muted fs-12">{formatRelativeTime(notif.createdAt)}</small>
          </div>
        </div>
      );
    });
  };

  return (
    <div className="dropdown pe-1">
      <Link
        to="#"
        className="btn btn-outline-light bg-white btn-icon position-relative me-1"
        data-bs-toggle="dropdown"
      >
        <i className="ti ti-bell" />
        {unreadCount > 0 && (
          <span className="notification-status-dot">
          </span>
        )}
      </Link>

      <div className="dropdown-menu dropdown-menu-end notification-dropdown p-3" style={{ minWidth: '300px' }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">{t('notifications.title')} ({unreadCount})</h5>
          <button className="btn btn-sm btn-link text-primary p-0" onClick={markAllAsRead}>
            {t('notifications.mark_all_read')}
          </button>
        </div>

        <div className="noti-content" style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {renderContent()}

          {/* Load more inside scroll */}
          {pagination.page * pagination.limit < pagination.total && !loading && !error && (
            <div className="text-center mt-3">
              <button
                className="btn btn-outline-primary btn-sm"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); fetchNextPage(); }}
              >
                {t('notifications.load_more')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationDropdown;